import { useCallback, useRef, type MouseEvent as ReactMouseEvent } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import { cssMap } from '@atlaskit/css';

import Heading from '@atlaskit/heading/heading';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';

import ExitingPersistence from '@atlaskit/motion/exiting-persistence';

import FadeIn from '@atlaskit/motion/fade-in';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Text } from '@atlaskit/primitives/compiled/text';

import { Pressable } from '@atlaskit/primitives/compiled/pressable';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { token } from '@atlaskit/tokens';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { defaultMessages } from '../messages';

import type { ChecklistItem } from '../types';

import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	card: {
		width: '100%',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		textAlign: 'start',

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	completedMainContent: {
		opacity: token('opacity.disabled'),
	},
	completedLabel: {
		textDecoration: 'line-through',
	},
});

export type ChecklistCardPagination = {
	onPrevious: () => void;
	onNext: () => void;
	canGoPrevious: boolean;
	canGoNext: boolean;
};

type ChecklistCardProps = {
	checklistItem: ChecklistItem;
	isCompleted: boolean;
	onClick: () => void;
	pagination: ChecklistCardPagination;
};

const iconByTheme = (item: ChecklistItem, colorMode: 'light' | 'dark') => {
	const ImageLight = item.iconLight;
	const ImageDark = item.iconDark;

	return colorMode === 'light' ? <ImageLight /> : <ImageDark />;
};

export const ChecklistCard = ({
	checklistItem,
	isCompleted,
	onClick,
	pagination,
}: ChecklistCardProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();

	const label = formatMessage(checklistItem.label, checklistItem.labelValues);
	const description = formatMessage(checklistItem.description, checklistItem.labelValues);
	const firstRef = useRef<HTMLElement | null>(null);

	const onTryItButtonClick = useCallback(
		(e: ReactMouseEvent) => {
			e.stopPropagation();
			void onClick();
		},
		[onClick],
	);

	const onPaginationPrevious = useCallback(
		(e: ReactMouseEvent) => {
			e.stopPropagation();
			pagination.onPrevious();
		},
		[pagination],
	);

	const onPaginationNext = useCallback(
		(e: ReactMouseEvent) => {
			e.stopPropagation();
			pagination.onNext();
		},
		[pagination],
	);

	return (
		<Pressable
			xcss={styles.card}
			testId={`checklist-card-${checklistItem.id}`}
			onClick={onTryItButtonClick}
			componentName="rovo-onboarding-checklist-card"
		>
			<Inline space="space.300" alignBlock="center" spread="space-between">
				<ExitingPersistence appear>
					<FadeIn key={checklistItem.id}>
						{(motionProps) => (
							<div {...motionProps}>
								<Box xcss={isCompleted ? styles.completedMainContent : undefined}>
									<Inline space="space.150" alignBlock="start" grow="fill">
										<Stack space="space.075">
											<Inline space="space.050" alignBlock="center">
												{iconByTheme(checklistItem, colorMode ?? 'light')}
												<Box xcss={isCompleted && styles.completedLabel}>
													<Heading size="small">
														<Text ref={firstRef} size="medium" weight="bold" maxLines={4}>
															{label}
														</Text>
													</Heading>
												</Box>
											</Inline>
											<Text size="small" color="color.text.subtlest" maxLines={4}>
												{description}
											</Text>
										</Stack>
									</Inline>
								</Box>
							</div>
						)}
					</FadeIn>
				</ExitingPersistence>
				{pagination && (
					<Inline space="space.100" alignBlock="center" alignInline="end" shouldWrap>
						{/* eslint-disable-next-line @post-office/message-linting/require-instrumented-interactions -- OnboardingChecklist.onItemClick records the item action through the onClick callback. */}
						<Button
							appearance="default"
							onClick={onTryItButtonClick}
							testId={`checklist-card-try-it-${checklistItem.id}`}
						>
							{formatMessage(defaultMessages.tryItButton)}
						</Button>
						<Inline space="space.100" alignBlock="center">
							<IconButton
								icon={(iconProps) => (
									<ChevronLeftIcon
										{...iconProps}
										size="small"
										color={
											!pagination.canGoPrevious ? token('color.text.disabled') : token('color.text')
										}
									/>
								)}
								label={formatMessage(defaultMessages.previousChecklistCard)}
								appearance="subtle"
								spacing="compact"
								isDisabled={!pagination.canGoPrevious}
								onClick={onPaginationPrevious}
								testId="checklist-pagination-left"
							/>
							<IconButton
								icon={(iconProps) => (
									<ChevronRightIcon
										{...iconProps}
										size="small"
										color={
											!pagination.canGoNext ? token('color.text.disabled') : token('color.text')
										}
									/>
								)}
								label={formatMessage(defaultMessages.nextChecklistCard)}
								appearance="subtle"
								spacing="compact"
								isDisabled={!pagination.canGoNext}
								onClick={onPaginationNext}
								testId="checklist-pagination-right"
							/>
						</Inline>
					</Inline>
				)}
			</Inline>
		</Pressable>
	);
};
